import { useState, type FormEvent } from 'react';
import { Alert, Button, Eyebrow, Field, Input } from '@busbook/ui';
import { api } from '../../lib/api.js';
import { useT } from '../../i18n/index.js';

/**
 * Adding a booking made before the passenger had an account.
 *
 * Booking requires an account now (DECISIONS D68), so this is not a way IN — it is how
 * somebody who booked as a guest last month puts that journey where the rest of their trips
 * are. Bookings made on this device are adopted silently at sign-in; this is the same thing
 * for a booking made somewhere else.
 *
 * The proof is the reference and the address the ticket was sent to, which is exactly what
 * guest retrieval already asked for. Only signed-in passengers see this form, so unlike
 * retrieval it can say plainly whether the booking was found: withholding that from someone
 * asking about their own trip is not privacy, it is a dead end.
 */
export function ClaimForm({ onClaimed }: { onClaimed: () => void }) {
  const t = useT();
  const [reference, setReference] = useState('');
  const [email, setEmail] = useState('');
  const [result, setResult] = useState<'claimed' | 'not-found' | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const { claimed } = await api.request<{ claimed: boolean }>('/public/bookings/claim', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ reference: reference.trim(), email: email.trim() }),
      });
      setResult(claimed ? 'claimed' : 'not-found');
      // Reloading the list is the point of the form; a success message about a trip that has
      // not appeared reads as a failure.
      if (claimed) {
        setReference('');
        setEmail('');
        onClaimed();
      }
    } catch {
      setResult('not-found');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="rounded-xl border p-4"
      style={{ borderColor: 'var(--color-line)', background: 'var(--color-surface)' }}
    >
      <Eyebrow>{t('m.claim')}</Eyebrow>
      <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
        {t('m.claimHint')}
      </p>

      <form onSubmit={submit} className="mt-3 flex flex-col gap-3">
        <Field label={t('m.reference')} htmlFor="claim-ref">
          <Input
            id="claim-ref"
            className="tabular"
            placeholder="RF-8K3D2"
            autoComplete="off"
            value={reference}
            onChange={(e) => setReference(e.target.value.toUpperCase())}
            required
          />
        </Field>
        <Field label={t('c.email')} htmlFor="claim-email">
          <Input
            id="claim-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Field>

        {result === 'claimed' && <Alert tone="success">{t('m.claimed')}</Alert>}
        {result === 'not-found' && <Alert tone="error">{t('m.claimNotFound')}</Alert>}

        <Button type="submit" variant="secondary" block loading={busy}>
          {t('m.claimAction')}
        </Button>
      </form>
    </div>
  );
}
